import React, { useState } from 'react';
/**
 * AuthGuard — Authentication + Onboarding screen rendering
 *
 * Extracted from App.tsx in R34 (DDD Fase 6 — App Shell decomposition).
 * Renders WelcomeScreen, LoginScreen, or RegisterScreen when the user
 * is not authenticated.
 *
 * After authentication, if `hasCompletedOnboarding` is false/undefined,
 * the OnboardingWizard is shown:
 * - Learner → 1-staps (welkom) → home
 * - Parent  → 1-staps (welkom) → portaal empty-state met inline koppelcode
 * - Teacher → 1-staps (welkom) → portaal empty-state met inline klas-aanmaak
 * - Admin   → onboarding wordt overgeslagen
 *
 * De leerling had hier een tweede stap (groep/vak-keuze). Die is vervallen —
 * `OnboardingWizard` zet `totalSteps = 1` voor álle rollen. Deze docstring
 * beschreef de oude flow nog; gecorrigeerd 16 aug 2026.
 *
 * Alle child/classroom-linking gebeurt voortaan in de portal-empty-states,
 * niet meer in de wizard (Sprint 2.8 — zie AUTH_ONBOARDING_DOMAIN_RC_AUDIT_2026.md §23).
 */
import { WelcomeScreen, LoginScreen, RegisterScreen, OnboardingWizard, SetNewPasswordScreen, isPasswordRecoveryLanding, type AccountsState, type Account, type OnboardingStepState } from '../accounts';
import type { CreateAccountDetails, CreateAccountOptions } from './store/types';
interface AuthGuardProps {
  showWelcome: boolean;
  currentNavView: string;
  accounts: AccountsState;
  activeAccount: Account | null;
  onNavigateToLogin: () => void;
  onNavigateToRegister: () => void;
  onLoginSuccess: (accountId: string) => void;
  onBack: () => void;
  createAccount: (role: 'learner' | 'teacher' | 'parent' | 'admin', details: CreateAccountDetails, options?: CreateAccountOptions) => string;
  // Onboarding callbacks
  onCompleteOnboarding: (stepState?: OnboardingStepState) => void;
  onNavigateToModule?: (moduleId: string) => void;
  onNavigateToMyClass?: () => void;
  children: React.ReactNode;
}
export function AuthGuard({
  showWelcome,
  currentNavView,
  accounts,
  activeAccount,
  onNavigateToLogin,
  onNavigateToRegister,
  onLoginSuccess,
  onBack,
  createAccount,
  onCompleteOnboarding,
  onNavigateToModule,
  onNavigateToMyClass,
  children
}: AuthGuardProps): React.ReactElement {
  const [onboardingError, setOnboardingError] = useState<string | undefined>();
  const [onboardingLoading, setOnboardingLoading] = useState(false);

  /*
   ── Herstellink uit de e-mail — VÓÓR alle andere schermen ───────────────────
    Dit moet de eerste tak zijn. Supabase heeft de tokens uit de hash op dat
   moment al omgezet in een geldige sessie, dus zonder deze tak is de gebruiker
   gewoon INGELOGD en landt hij in de app — zonder ooit een nieuw wachtwoord te
   kunnen zetten, en zonder te begrijpen waarom de link "werkte" maar niets deed.
    Dat is ook waarom `showWelcome` hier niet voorgaat: een herstellink is een
   expliciete handeling van de gebruiker, en die weegt zwaarder dan de
   standaardstart van de app.
    De detectie leest de hash synchroon bij het laden van de module — zie
   `recoveryLink.ts` voor waarom dat niet in een effect kan.
   */
  const [recoveryHandled, setRecoveryHandled] = useState(false);
  if (!recoveryHandled && isPasswordRecoveryLanding()) {
    return <SetNewPasswordScreen onDone={() => {
      setRecoveryHandled(true);
      onNavigateToLogin();
    }} data-id="element-650" data-mp="hvs5vce4oep7" />;
  }

  // ── Pre-auth screens ───────────────────────────────────────
  if (showWelcome) {
    return <WelcomeScreen onLogin={onNavigateToLogin} onRegister={onNavigateToRegister} data-id="element-651" data-mp="1ak649t94pd1a" />;
  }
  if (currentNavView === 'login') {
    return <LoginScreen onLoginSuccess={onLoginSuccess} onBack={onBack} onNavigateToRegister={onNavigateToRegister} data-id="element-652" data-mp="15mxjdkwliuwc" />;
  }
  if (currentNavView === 'register') {
    return <RegisterScreen onRegisterSuccess={onLoginSuccess} onBack={onBack} onNavigateToLogin={onNavigateToLogin} accounts={accounts} createAccount={createAccount} data-id="element-653" data-mp="wm94yp3aisix" />;
  }
  // ── Post-auth onboarding ───────────────────────────────────
  // Show onboarding for newly registered accounts that haven't completed it.
  // Admins skip onboarding entirely.
  if (activeAccount && !activeAccount.hasCompletedOnboarding && activeAccount.role !== 'admin') {
    // De drie `// Stubbed for backwards compatibility`-props zijn hier weg
    // (16 aug 2026). Ze stonden op `{ success: false }` en werden door de wizard
    // genegeerd; zie de toelichting in OnboardingWizard.tsx.
    return <OnboardingWizard account={activeAccount} accounts={accounts} onCompleteOnboarding={onCompleteOnboarding} onNavigateToModule={onNavigateToModule} onNavigateToMyClass={onNavigateToMyClass} data-id="element-654" data-mp="1rtgqwbbqjtk5" />;
  }
  return <>{children}</>;
}